// components/multisig/ApproveMintRequests.js

import React, { useEffect, useState, useCallback } from "react";
import Web3 from "web3";
import { contractABI, contractAddress } from "../../contractConfig";

// Always-available RPC provider for reads
const RPC_URL = "https://polygon-amoy.drpc.org";
const readOnlyWeb3 = new Web3(new Web3.providers.HttpProvider(RPC_URL));

const ApproveMintRequests = () => {
  const [mintRequests, setMintRequests] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState({});

  // ---- Read-only contract (no wallet required) ----
  const getReadContract = () => {
    return new readOnlyWeb3.eth.Contract(contractABI, contractAddress);
  };

  // ---- Only for sending tx ----
  const ensureWallet = async () => {
    if (!window.ethereum) throw new Error("MetaMask not installed");
    let accs = await window.ethereum.request({ method: "eth_accounts" });
    if (!accs || accs.length === 0) {
      accs = await window.ethereum.request({ method: "eth_requestAccounts" });
    }
    const web3 = new Web3(window.ethereum);
    return { web3, account: accs[0] };
  };

  const fetchMintRequests = useCallback(async () => {
    setLoading(true);
    const contract = getReadContract();

    try {
      const requests = [];
      const totalRequests = await contract.methods.getTotalMintRequests().call();

      if (Number(totalRequests) === 0) {
        setMessage("No mint requests available.");
      } else {
        for (let i = 0; i < totalRequests; i++) {
          const requestId = i + 1;

          const pendingSigners = await contract.methods.getPendingMintRequests(requestId).call();
          const approvalsCompleted = await contract.methods.approvalsCompleted(requestId).call();
          const request = await contract.methods.mintRequests(requestId).call();

          // Fetch metadata (IPFS gateway normalize)
          let tokenMetadata = { name: "Unknown", description: "No description available" };
          try {
            let fetchURI = request.metaURI;
            if (fetchURI.startsWith("ipfs://")) {
              fetchURI = `https://gateway.pinata.cloud/ipfs/${fetchURI.replace("ipfs://", "")}`;
            }
            const res = await fetch(fetchURI);
            if (res.ok) tokenMetadata = await res.json();
          } catch (e) {
            console.error(`Metadata fetch failed for ${requestId}:`, e);
          }

          requests.push({
            requestId,
            to: contractAddress,
            metaURI: request.metaURI,
            pendingSigners,
            approvalsCompleted,
            tokenName: tokenMetadata.name,
            tokenDescription: tokenMetadata.description,
          });
        }
      }
      setMintRequests(requests);
    } catch (err) {
      console.error("Error fetching mint requests:", err);
      setMessage("Failed to fetch mint requests: " + (err.message || err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMintRequests();
  }, [fetchMintRequests]);

  const handleMintRequest = async (requestId) => {
    setLoadingRequests((s) => ({ ...s, [requestId]: true }));
    setMessage("");

    try {
      const { web3, account } = await ensureWallet();
      const contract = new web3.eth.Contract(contractABI, contractAddress);

      const gasPrice = await web3.eth.getGasPrice();
      const gas = await contract.methods.approveMint(requestId).estimateGas({ from: account });

      const tx = await contract.methods.approveMint(requestId).send({ from: account, gas, gasPrice });

      setMessage(`Mint request approved! Tx: ${tx.transactionHash}`);

      // Refresh locally
      setMintRequests((prev) =>
        prev.map((r) =>
          r.requestId === requestId ? { ...r, pendingSigners: [], approvalsCompleted: true } : r
        )
      );
    } catch (err) {
      console.error("Transaction Error:", err);
      if (err?.code === -32002) {
        setMessage("A MetaMask request is already pending. Open MetaMask to complete it, then retry.");
      } else {
        setMessage("Error processing mint request: " + (err.message || err));
      }
    } finally {
      setLoadingRequests((s) => ({ ...s, [requestId]: false }));
    }
  };

  return (
    <div className="container" style={{ marginBottom: "30px" }}>
      <h2>Approve Requests to Issue Licences</h2>
      <p>
        <strong>Note:</strong>{' '}
        <span style={{ color: 'red', fontWeight: 'bold' }}>
          Only signers can approve the new licence request
        </span>
      </p>

      {message && <p>{message}</p>}
      {loading ? (
        <p>Loading licence requests...</p>
      ) : mintRequests.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between" }}>
          {mintRequests.map((request) => (
            <div
              key={request.requestId}
              style={{
                width: "48%",
                marginBottom: "20px",
                border: "1px solid #ccc",
                padding: "10px",
                boxSizing: "border-box",
                wordWrap: "break-word",
              }}
            >
              <p><strong>Request ID:</strong> {request.requestId}</p>
              <p><strong>Licence Name:</strong> {request.tokenName || "N/A"}</p>
              <p><strong>Licence Description:</strong> {request.tokenDescription || "N/A"}</p>
              <p><strong>Licence Identifier:</strong> {request.metaURI}</p>
              <p><strong>Issue To:</strong> {request.to}</p>

              <div>
                <strong>Pending Approvals:</strong>
                {request.approvalsCompleted ? (
                  <span style={{ color: "green", marginLeft: "5px" }}>
                    <strong>All approvals completed</strong>
                  </span>
                ) : request.pendingSigners.length > 0 ? (
                  <ul>
                    {request.pendingSigners.map((signer, index) => (
                      <li key={index} style={{ color: "red", marginBottom: "5px" }}>
                        <div style={{ wordBreak: "break-word", fontSize: "14px" }}>
                          {signer}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span style={{ color: "green", marginLeft: "5px" }}>
                    <strong>Approval Completed</strong>
                  </span>
                )}
              </div>

              <button
                type="button"
                onClick={() => handleMintRequest(request.requestId)}
                disabled={loadingRequests[request.requestId] || request.approvalsCompleted}
              >
                {loadingRequests[request.requestId] ? "Approving..." : "Approve the mint"}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p>No mint requests available.</p>
      )}
    </div>
  );
};

export default ApproveMintRequests;